import type {MaybeRef} from 'vue';
import {map} from 'lodash-es';
import {storeToRefs} from 'pinia';
import type {ReadonlyDeep} from 'type-fest';
import {extractGasStationOpeningHours, type OpeningHoursEntity} from '~/utils/openingHours';
import type {AutomaticCarWashStoryblok, PageTypeGasStationStoryblok} from '~/storyblok/types';
import {useStoryblokGasStation} from '~/stores/storyblokGasStation';
import {useStoryblokServices} from '~/stores/storyblokServices';
import {useStoryblokHypermarketsStore} from '~/stores/storyblokHypermarkets';

export interface HypermarketDetails {
	openingHourEntities: OpeningHoursEntity[]
	gasStation?: PageTypeGasStationStoryblok
	carWash?: AutomaticCarWashStoryblok
	services?: any[];
}

export interface UseHypermarketDetailsReturn {
	data: ReadonlyDeep<Ref<HypermarketDetails | null>>
	loading: ReadonlyDeep<Ref<boolean>>
}

export default function useHypermarketDetails(slug: MaybeRef<string | null | undefined>): UseHypermarketDetailsReturn {
	const gasStationStore = useStoryblokGasStation();
	const servicesStore = useStoryblokServices();
	const hypermarketsStore = useStoryblokHypermarketsStore();

	const {hypermarketsWithGlobusBySlug} = storeToRefs(hypermarketsStore);

	const details = ref<HypermarketDetails | null>(null);
	const loading = ref<boolean>(false);

	const hypermarket = computed(() => {
		const resolvedSlug = unref(slug);
		return resolvedSlug
			? hypermarketsWithGlobusBySlug.value[resolvedSlug]
			: null;
	});

	watch(hypermarket, () => {
		// Wrap in arrow function to handle async errors properly
		fetchDetails();
	}, {immediate: true});

	return {
		data: readonly(details),
		loading: readonly(loading),
	};

	async function fetchDetails() {
		if (!hypermarket.value) {
			details.value = null;
			loading.value = false;
			return;
		}

		loading.value = true;

		try {
			const [gasStationResponse, services] = await Promise.all([
				gasStationStore.fetch(hypermarket.value.slug),
				servicesStore.fetch(hypermarket.value.slug),
			]);

			const gasStation = unref(gasStationResponse);

			let gasStationData: (PageTypeGasStationStoryblok & {fullSlug: string}) | undefined;
			let carWashData: AutomaticCarWashStoryblok | undefined;

			if (gasStation?.content) {
				const {pricelist, fuelPricelist, automaticCarWash, ...restContent} = gasStation.content;

				gasStationData = {
					...restContent,
					...(typeof pricelist === 'object' && {pricelist}),
					...(typeof fuelPricelist === 'object' && {fuelPricelist}),
					...(Array.isArray(automaticCarWash) && {automaticCarWash}),
					fullSlug: gasStation.full_slug,
				} as PageTypeGasStationStoryblok & {fullSlug: string};

				if (Array.isArray(automaticCarWash) && automaticCarWash.length > 0) {
					carWashData = automaticCarWash[0] as AutomaticCarWashStoryblok;
				}
			}

			details.value = {
				openingHourEntities: [
					{
						name: 'Hypermarket',
						hours: hypermarket.value.content.openingHours?.openingHours ?? [],
						holidays: hypermarket.value.content.holidayOpeningHours,
						open: hypermarket.value.content.openingHours?.openingHours.some((item: any) => item.isOpen === true),
						fullSlug: dropUnderscoreRouteParts(hypermarket.value.full_slug),
					},
					...map(services, (service) => ({
						component: service.content.component,
						name: service.content.title,
						hours: service.content.openingHours?.openingHours ?? [],
						holidays: service.content.holidayOpeningHours,
						open: service.content.state === 'open',
						fullSlug: dropUnderscoreRouteParts(service.full_slug),
					})),
					...gasStation ? extractGasStationOpeningHours(gasStation) : [],
				],
				gasStation: gasStationData,
				carWash: carWashData,
				services,
			};
		} catch (error: any) {
			// Show error page with extracted statusCode from nested error
			useShowError(error);
		} finally {
			loading.value = false;
		}
	}
}
